// CrazyGamesSdk — thin wrapper around the official CrazyGames SDK that
// loads lazily from the portal CDN at runtime. When the SDK is not
// available (any non-portal host, all of localhost development, every
// test environment) the wrapper degrades to no-ops so nothing else in
// the codebase has to branch on environment.
//
// Public surface (intentionally tiny):
//   - ready()              — resolves once the SDK has finished init.
//                            No-op-resolved when not on the portal.
//   - gameplayStart()      — call when a match becomes interactive.
//   - gameplayStop()       — call when the match ends or the player
//                            navigates back to the menu.
//   - happytime()          — call when the player wins (positive moment).
//   - midgameAd()          — request a midgame ad. Returns a Promise that
//                            resolves when the ad finishes (or
//                            immediately if no ad is shown).
//   - rewardedAd()         — request a rewarded ad; resolves to true
//                            when the player watched, false otherwise.
//
// CrazyGames requires gameplayStart / gameplayStop to drive their
// session metrics; ads are gated by the same lifecycle. See
// docs.crazygames.com / sdk-v3.

const SDK_URL = 'https://sdk.crazygames.com/crazygames-sdk-v3.js';

interface CrazyGamesV3 {
  init: () => Promise<void>;
  game: {
    gameplayStart: () => void;
    gameplayStop: () => void;
    happytime: () => void;
    sdkGameLoadingStart?: () => void;
    sdkGameLoadingStop?: () => void;
    // Portal-set boolean. True when the player launched the game from a
    // "Play with friends" entry point and should be routed straight into
    // a multiplayer room as the party leader. Required by CrazyGames for
    // multiplayer-tagged games.
    isInstantMultiplayer?: boolean;
    // Portal-native invite button. Surfacing this satisfies the
    // "use invite link & button functionality" requirement; the params
    // object is round-tripped back to the invitee via `getInviteParam`.
    showInviteButton?: (params: Record<string, string>) => Promise<void>;
    hideInviteButton?: () => void;
    // V4.2 — per the official multiplayer SDK docs, `inviteLink` returns
    // a SYNC string (the example shows `const link = SDK.game.inviteLink({...})`).
    // We typed it as Promise-or-string to absorb either shape.
    inviteLink?: (
      params: Record<string, string>,
    ) => string | Promise<string>;
    getInviteParam?: (key: string) => string | null | undefined;
    // V4.2 — canonical multiplayer room API per
    // https://docs.crazygames.com/sdk/game/#multiplayer-features.
    // `updateRoom` registers the current room with the platform so the
    // native invite button appears, platform notifications fire, and
    // the friends drawer can join. Required BEFORE `inviteLink` /
    // `showInviteButton` will produce working invites for a multiplayer
    // session.
    updateRoom?: (opts: {
      roomId?: string;
      isJoinable?: boolean;
      inviteParams?: Record<string, string>;
    }) => void;
    leftRoom?: () => void;
    // Invite-while-in-game listener. Fires when a player who is already
    // inside the running game clicks an invite notification from a
    // friend. The game should switch to the invited room (the
    // alternative scenario — invite click from outside the game — uses
    // `inviteParams` on next boot).
    addJoinRoomListener?: (
      listener: (params: Record<string, string>) => void,
    ) => void;
    removeJoinRoomListener?: (
      listener: (params: Record<string, string>) => void,
    ) => void;
    // Set by the portal when the game was launched from an invite link.
    // Null when the game was launched directly. Per the docs this is
    // the canonical source; `getInviteParam('key')` is a key-level
    // accessor over the same data.
    inviteParams?: Record<string, string> | null;
  };
  ad: {
    requestAd: (type: 'midgame' | 'rewarded', callbacks?: {
      adStarted?: () => void;
      adError?: (err: unknown) => void;
      adFinished?: () => void;
    }) => Promise<void>;
  };
  user: unknown;
  // Legacy v2 surface — kept as a fallback for any SDK build that still
  // exposes invite/data outside of `game`.
  invite?: {
    inviteLink?: (params: Record<string, string>) => Promise<string>;
    showInviteButton?: (params: Record<string, string>) => Promise<void>;
  };
  data?: {
    getInviteParam?: (key: string) => string | null | undefined;
    // V1.8.4 — Cloud Data Module (SDK v3). When the player is signed in to
    // CrazyGames, these calls persist per-user across devices; otherwise
    // they fall back to the portal's own per-browser storage. We use them
    // to mirror the local-only `signalclash.*` localStorage keys so
    // cosmetic unlocks, daily-challenge progress, settings, etc. survive
    // a tab close on the portal (LocalStorage alone doesn't, because the
    // game runs inside an iframe whose origin doesn't match the portal's
    // own storage scope).
    getItem?: (key: string) => Promise<string | null> | string | null;
    setItem?: (key: string, value: string) => Promise<void> | void;
    removeItem?: (key: string) => Promise<void> | void;
  };
}

// All `signalclash.*` localStorage keys that should mirror to the CrazyGames
// cloud Data Module. Listing them explicitly (rather than scanning by
// prefix at runtime) keeps the contract auditable and means a typo at the
// call site can't accidentally sync a new key without our knowing.
export const CLOUD_SAVE_KEYS: readonly string[] = [
  // Profile + progression
  'signalclash.profile.uuid',
  'signalclash.telemetry.v1',
  // V1.9 — XP/level + login-streak + last-visit anchor. Synced so a
  // player who plays on desktop then opens on phone tomorrow sees
  // their streak extend (rather than reset) and their XP bar carry
  // over. Without this the new progression surfaces feel broken to
  // cross-device users.
  'signalclash.profile.xp',
  'signalclash.profile.streak',
  'signalclash.profile.lastVisit',
  // V2 Tempo — Skill Ladder + Squad Mastery. Same cross-device sync
  // rationale: a player whose Diamond rank reset because they opened
  // the game on a new device would feel cheated.
  'signalclash.profile.elo',
  'signalclash.profile.eloPeak',
  'signalclash.profile.squadMastery',
  // Settings + audio mix
  'signalclash.settings.v1',
  'signalclash.audio.master',
  'signalclash.audio.sfx',
  'signalclash.audio.music',
  'signalclash.audio.voice',
  // First-run UI flags (so a returning player on a new device doesn't
  // re-see the tutorial / intro / hints they already dismissed elsewhere).
  'signalclash.intro.seen',
  'signalclash.tutorial.completed',
  // V4.1 cohort flag — abilities coach (second-match Q-button lesson).
  // Cross-device sync prevents the coach from re-firing on a player's
  // phone after they finished it on desktop.
  'signalclash.tutorial.abilities_completed',
  // V1.9 cohort flag — macro coach (matches 3-9 passive timed toasts).
  // Synced so dismissing the toasts on one device sticks on the next.
  'signalclash.tutorial.macro_completed',
  'signalclash.camera_hint.seen',
  'signalclash.mobileBanner.dismissed',
  'signalclash:orientationHintDismissed',
];

declare global {
  interface Window {
    CrazyGames?: { SDK?: CrazyGamesV3 };
  }
}

// Hard ceiling on the SDK script + init wait. A stalled script tag
// (slow CDN, captive portal, ad blocker that delays without rejecting)
// never fires `load` or `error`, which would otherwise leave
// `ready()` pending forever and the portal stuck on its loading
// overlay. Resolving after this timeout lets the rest of the boot
// continue; the wrapper transparently no-ops every SDK call until /
// unless the script actually arrives later.
const SDK_LOAD_TIMEOUT_MS = 10_000;

export class CrazyGamesSdk {
  private sdk: CrazyGamesV3 | null = null;
  private readyPromise: Promise<void> | null = null;
  private gameplayActive = false;
  // Queued loadingStart/Stop intent. Callers (ClientApp boot,
  // MainMenuScene.create) fire these BEFORE the SDK script has
  // finished loading. Without queuing, an early sdkGameLoadingStop
  // would be lost and a late sdkGameLoadingStart would arrive AFTER
  // the stop — the portal would see an unmatched STOP-then-START
  // pair, keep its loading overlay up, and bucket the session as a
  // failed load. The booleans track the *final intent* (last call
  // wins) and are replayed in order once `this.sdk` is bound.
  private pendingLoadingStart = false;
  private pendingLoadingStop = false;
  // Detect whether we're inside the CrazyGames frame. The portal
  // serves the game inside an iframe with a specific referrer. We
  // best-effort detect by checking for the host's known referrer
  // pattern; if anything is off we fall back to disabled.
  private readonly enabled: boolean = (() => {
    if (typeof window === 'undefined') return false;
    // Allow opt-in via build flag for local testing of the SDK path.
    const force = (import.meta.env as { VITE_CRAZYGAMES?: string }).VITE_CRAZYGAMES === '1';
    if (force) return true;
    try {
      const inFrame = window.self !== window.top;
      if (!inFrame) return false;
      const ref = document.referrer || '';
      return /crazygames\.com|1001spiele\.de|jopi\.com/i.test(ref);
    } catch {
      // Cross-origin frame access throws — but that itself is a strong
      // signal we're embedded somewhere, so enable.
      return true;
    }
  })();

  isEnabled(): boolean {
    return this.enabled;
  }

  // Load the SDK script and resolve once init() completes. Idempotent —
  // multiple ready() calls share the same promise.
  ready(): Promise<void> {
    if (this.readyPromise !== null) return this.readyPromise;
    if (!this.enabled || typeof document === 'undefined') {
      this.readyPromise = Promise.resolve();
      return this.readyPromise;
    }
    this.readyPromise = new Promise<void>((resolve) => {
      let settled = false;
      const finish = (): void => {
        if (settled) return;
        settled = true;
        // Replay any queued loadingStart/Stop intent now that the SDK
        // is (or isn't) bound. If both start and stop were called
        // while we were loading, fire them in that order so the
        // portal sees a clean pair instead of an unmatched stop.
        this.flushPendingLoadingEvents();
        resolve();
      };
      const script = document.createElement('script');
      script.src = SDK_URL;
      script.async = true;
      script.onload = () => {
        const sdk = window.CrazyGames?.SDK;
        if (sdk === undefined) {
          // Script loaded but global not present — treat as disabled.
          finish();
          return;
        }
        sdk
          .init()
          .then(() => {
            this.sdk = sdk;
            finish();
          })
          .catch(() => {
            // Init failures degrade to no-op — never block the game.
            finish();
          });
      };
      script.onerror = () => finish();
      // Hard timeout: if the script tag never fires load or error
      // (stalled CDN, blocked network), unblock the rest of the boot
      // anyway. The portal still gets a working game — just without
      // the optional SDK features.
      setTimeout(finish, SDK_LOAD_TIMEOUT_MS);
      document.head.appendChild(script);
    });
    return this.readyPromise;
  }

  // Replay queued loadingStart/Stop intent once the SDK is bound (or
  // skipped). Call sites use sdkGameLoadingStart() *before* assets
  // begin generating and sdkGameLoadingStop() once the menu is
  // interactive — but ready() can resolve AFTER both of those have
  // already happened. Replaying here guarantees the portal sees a
  // start-then-stop pair (or neither) regardless of timing.
  private flushPendingLoadingEvents(): void {
    if (this.pendingLoadingStart) {
      try {
        this.sdk?.game.sdkGameLoadingStart?.();
      } catch {
        // Optional SDK method — swallow.
      }
    }
    if (this.pendingLoadingStop) {
      try {
        this.sdk?.game.sdkGameLoadingStop?.();
      } catch {
        // Optional SDK method — swallow.
      }
    }
  }

  gameplayStart(): void {
    if (this.gameplayActive) return;
    this.gameplayActive = true;
    try {
      this.sdk?.game.gameplayStart();
    } catch {
      // SDK exception — swallow; never block gameplay.
    }
  }

  gameplayStop(): void {
    if (!this.gameplayActive) return;
    this.gameplayActive = false;
    try {
      this.sdk?.game.gameplayStop();
    } catch {
      // see gameplayStart
    }
  }

  // Signal that game assets have started loading. Call immediately before
  // heavy asset work begins so the portal can display its own loading UX.
  // Safe to call BEFORE `ready()` resolves — the intent is queued and
  // replayed once the SDK is bound. If the SDK is already bound, fires
  // straight through.
  sdkGameLoadingStart(): void {
    this.pendingLoadingStart = true;
    if (this.sdk === null) return;
    try {
      this.sdk.game.sdkGameLoadingStart?.();
    } catch {
      // Optional SDK method — swallow.
    }
  }

  // Signal that game assets have finished loading and the game is interactive.
  // Call once the main menu (or first game screen) is ready to accept input.
  // Same queuing semantics as sdkGameLoadingStart.
  sdkGameLoadingStop(): void {
    this.pendingLoadingStop = true;
    if (this.sdk === null) return;
    try {
      this.sdk.game.sdkGameLoadingStop?.();
    } catch {
      // Optional SDK method — swallow.
    }
  }

  // Positive-moment hook (player wins, reaches milestone). The portal
  // uses this to coordinate "feel-good" timing for ads / reviews.
  happytime(): void {
    try {
      this.sdk?.game.happytime();
    } catch {
      // see above
    }
  }

  // Request a midgame interstitial. Resolves regardless of whether an
  // ad was actually shown. Audio context is paused on the caller side
  // (PauseController) — this method is fire-and-forget.
  async midgameAd(): Promise<void> {
    if (this.sdk === null) return;
    return new Promise<void>((resolve) => {
      try {
        void this.sdk!.ad.requestAd('midgame', {
          adFinished: () => resolve(),
          adError: () => resolve(),
        });
      } catch {
        resolve();
      }
    });
  }

  async inviteLink(params: Record<string, string>): Promise<string | null> {
    const linkFn = this.sdk?.game?.inviteLink ?? this.sdk?.invite?.inviteLink;
    if (linkFn === undefined) return null;
    try {
      // Per the multiplayer-SDK docs the canonical signature is sync —
      // `const link = SDK.game.inviteLink({...})`. We await anyway so an
      // older Promise-returning shape still works (await on a non-thenable
      // resolves to the value).
      const result = await linkFn(params);
      return typeof result === 'string' && result.length > 0 ? result : null;
    } catch {
      return null;
    }
  }

  // V4.2 — multiplayer room registration. Per the docs, the canonical
  // flow is:
  //   1. updateRoom({ roomId: <unique-id> }) when the player joins.
  //   2. updateRoom({ isJoinable: true, inviteParams: {...} }) when the
  //      room is open for invitees. The platform shows the native invite
  //      button and exposes the inviteParams to joining players.
  //   3. updateRoom({ isJoinable: false }) when the room is full.
  //   4. leftRoom() when the player leaves.
  // No-op when the SDK isn't bound (local dev, off-portal) — the rest
  // of the game continues with our own room-code routing.
  updateRoom(opts: {
    roomId?: string;
    isJoinable?: boolean;
    inviteParams?: Record<string, string>;
  }): void {
    try {
      this.sdk?.game?.updateRoom?.(opts);
    } catch {
      // SDK exception — swallow. Multiplayer routing has a fallback
      // path (our own ?room=XXX URL parser) that works without the
      // platform-side room registration.
    }
  }

  leftRoom(): void {
    try {
      this.sdk?.game?.leftRoom?.();
    } catch {
      // see updateRoom
    }
  }

  // Returns the inviteParams object the player was launched with, or
  // null if they weren't launched from an invite. Canonical replacement
  // for `getInviteParam(key)` — we keep both because some flows want
  // the whole bag and some want a single key.
  getInviteParams(): Record<string, string> | null {
    try {
      const raw = this.sdk?.game?.inviteParams;
      if (raw === null || raw === undefined) return null;
      if (typeof raw !== 'object') return null;
      // Defensive shallow copy + type-narrow each value to string so a
      // bad SDK payload can't crash downstream consumers that expect
      // strings.
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(raw)) {
        if (typeof v === 'string') out[k] = v;
      }
      return out;
    } catch {
      return null;
    }
  }

  // Subscribe to invite-while-in-game events. The returned function
  // unsubscribes. Per the docs, this fires when the player is already
  // inside the game and clicks an invite notification from a friend —
  // the game should switch to the invited room. The complementary case
  // (invite click while NOT in game) is handled by `getInviteParams()`
  // on boot.
  addJoinRoomListener(
    listener: (params: Record<string, string>) => void,
  ): () => void {
    const addFn = this.sdk?.game?.addJoinRoomListener;
    const removeFn = this.sdk?.game?.removeJoinRoomListener;
    if (typeof addFn !== 'function') {
      // SDK not bound or doesn't expose the API — return a no-op
      // unsubscribe so callers can use the standard pattern without
      // null-checking.
      return () => undefined;
    }
    try {
      addFn(listener);
    } catch {
      return () => undefined;
    }
    return () => {
      try {
        removeFn?.(listener);
      } catch {
        // see updateRoom
      }
    };
  }

  getInviteParam(key: string): string | null {
    try {
      const getter =
        this.sdk?.game?.getInviteParam ?? this.sdk?.data?.getInviteParam;
      const raw = getter?.(key);
      if (typeof raw !== 'string') return null;
      const value = raw.trim();
      return value === '' ? null : value;
    } catch {
      return null;
    }
  }

  // Required by CrazyGames for multiplayer-tagged games: true when the
  // player launched from a portal "Play with friends" entry point. The
  // caller (boot path) routes the player straight into a fresh
  // multiplayer room as the party leader.
  isInstantMultiplayer(): boolean {
    try {
      return this.sdk?.game?.isInstantMultiplayer === true;
    } catch {
      return false;
    }
  }

  // Asks the portal to render its native invite button. Required by the
  // CrazyGames multiplayer checklist alongside the in-game invite link.
  // Idempotent — the SDK collapses repeated calls; we still guard so
  // we don't spam the call every render.
  async showInviteButton(params: Record<string, string>): Promise<void> {
    const fn = this.sdk?.game?.showInviteButton ?? this.sdk?.invite?.showInviteButton;
    if (fn === undefined) return;
    try {
      await fn(params);
    } catch {
      // Portal-side render failure — non-fatal, the in-game invite still
      // works because we also call inviteLink() on click.
    }
  }

  hideInviteButton(): void {
    try {
      this.sdk?.game?.hideInviteButton?.();
    } catch {
      // see showInviteButton
    }
  }

  // V1.8.4 — Cloud-save bootstrap. Called ONCE during pre-boot in main.ts
  // BEFORE any save module reads its localStorage values. Two phases:
  //
  //   1) HYDRATE — pull each known cloud-save key into localStorage so the
  //      synchronous reads later (Profile, Settings, AudioBus, ...) see the
  //      player's saved state instead of empty defaults.
  //   2) INSTALL — patch `window.localStorage.setItem` and `.removeItem` so
  //      that every future write for a registered key also pushes to the
  //      cloud Data Module (fire-and-forget, write-through).
  //
  // Both phases are wrapped in tight timeouts. If the SDK is not bound
  // (local dev, off-portal, ad-blocked CDN), or the cloud data API is
  // missing, or the network is slow, we skip cleanly and the game boots
  // with whatever localStorage already has — i.e. behaves exactly like
  // before this method existed.
  //
  // `keys` = the explicit allowlist of localStorage keys to mirror.
  // Anything outside this list never touches the SDK.
  async hydrateAndInstallCloudSave(keys: readonly string[]): Promise<void> {
    if (typeof localStorage === 'undefined') return;
    // V1.8 — diagnostic anchors. CrazyGames stats show D7 = 0% and
    // returning users = 6.57%. If hydrate silently fails for a chunk of
    // returning players, their localStorage looks empty on Day-2 and the
    // dashboard registers them as new — destroying retention measurement,
    // not just experience. The cloud_hydrate_status event lets us see
    // hydrate health in aggregate. Read into local var rather than `this`
    // so we can emit even after the method returns.
    const startedAt = Date.now();
    let outcome: 'success' | 'sdk_unavailable' | 'ready_timeout' | 'fetch_timeout' | 'error' =
      'sdk_unavailable';
    let keysHydrated = 0;
    let keysAttempted = 0;
    let lastError: string | null = null;
    let readyTimedOut = false;
    // Bounded wait for SDK ready. We don't want to block boot if the SDK
    // CDN is slow or blocked; 1.5 s is well past the typical portal init
    // (~200 ms). Off-portal `ready()` resolves immediately.
    await Promise.race([
      this.ready(),
      new Promise<void>((resolve) =>
        setTimeout(() => {
          readyTimedOut = true;
          resolve();
        }, 1500),
      ),
    ]);
    const data = this.sdk?.data;
    const getItem = data?.getItem;
    if (this.sdk === null || typeof getItem !== 'function') {
      // Not on portal OR SDK doesn't expose the Data Module. localStorage
      // continues to work locally; just no cloud mirroring.
      this.emitHydrateStatus({
        outcome: readyTimedOut ? 'ready_timeout' : 'sdk_unavailable',
        keysAttempted: 0,
        keysHydrated: 0,
        durationMs: Date.now() - startedAt,
      });
      return;
    }
    // Phase 1: HYDRATE.
    let fetchTimedOut = false;
    await Promise.race([
      Promise.all(
        keys.map(async (key) => {
          keysAttempted += 1;
          try {
            const raw = await getItem.call(data, key);
            if (typeof raw === 'string' && raw.length > 0) {
              localStorage.setItem(key, raw);
              keysHydrated += 1;
            }
          } catch (err) {
            lastError = err instanceof Error ? err.message : String(err);
          }
        }),
      ),
      new Promise<void>((resolve) =>
        setTimeout(() => {
          fetchTimedOut = true;
          resolve();
        }, 2000),
      ),
    ]);
    outcome = fetchTimedOut ? 'fetch_timeout' : lastError !== null ? 'error' : 'success';
    this.emitHydrateStatus({
      outcome,
      keysAttempted,
      keysHydrated,
      durationMs: Date.now() - startedAt,
      ...(lastError !== null ? { errorMessage: lastError } : {}),
    });
    // Phase 2: INSTALL write-through interceptor. Done AFTER hydrate so
    // the writes we just made (cloud → local) don't bounce back to cloud.
    this.installLocalStorageInterceptor(keys);
  }

  // Routes the hydrate diagnostic through window.dispatchEvent so the
  // analytics emitter can receive it without us pulling a circular
  // dependency on the analytics module (the SDK is created at boot,
  // before analytics is configured). main.ts wires the listener once.
  private emitHydrateStatus(payload: {
    outcome: 'success' | 'sdk_unavailable' | 'ready_timeout' | 'fetch_timeout' | 'error';
    keysAttempted: number;
    keysHydrated: number;
    durationMs: number;
    errorMessage?: string;
  }): void {
    if (typeof window === 'undefined') return;
    try {
      window.dispatchEvent(
        new CustomEvent('signalclash:cloud-hydrate-status', { detail: payload }),
      );
    } catch {
      // CustomEvent unavailable (very old browsers) — drop silently.
    }
  }

  // Wraps `localStorage.setItem` and `.removeItem` so writes to registered
  // keys also mirror to the cloud Data Module. Idempotent — re-installing
  // is harmless because we tag the wrapper to detect already-patched
  // methods and skip re-wrapping.
  private installLocalStorageInterceptor(keys: readonly string[]): void {
    if (typeof localStorage === 'undefined') return;
    const data = this.sdk?.data;
    const cloudSet = data?.setItem;
    const cloudRemove = data?.removeItem;
    if (typeof cloudSet !== 'function' && typeof cloudRemove !== 'function') return;
    const tracked = new Set(keys);
    // Tag the wrappers so a second hydrate call (defensive re-init,
    // hot-reload) doesn't double-wrap.
    interface Tagged { __sigclashWrapped?: true }
    const proto = Object.getPrototypeOf(localStorage) as Storage & Tagged;
    if (proto.__sigclashWrapped === true) return;
    proto.__sigclashWrapped = true;
    const origSet = proto.setItem.bind(localStorage);
    const origRemove = proto.removeItem.bind(localStorage);
    proto.setItem = function (key: string, value: string): void {
      origSet(key, value);
      if (tracked.has(key) && typeof cloudSet === 'function') {
        try {
          void cloudSet.call(data, key, value);
        } catch {
          // Cloud write failure is non-fatal; the local copy is intact.
        }
      }
    };
    proto.removeItem = function (key: string): void {
      origRemove(key);
      if (tracked.has(key) && typeof cloudRemove === 'function') {
        try {
          void cloudRemove.call(data, key);
        } catch {
          // see setItem
        }
      }
    };
  }
}
